import { useEffect, useState } from 'react';
import { ChevronLeft, CircleCheck } from 'lucide-react';
import Button from 'components/crm/ui/Button';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import { durationLabel } from 'helper/timeLabels';
import useText from 'hooks/useText';
import { getCallSession } from 'server/crm/prospectionApi';
import SectionHeader from './SectionHeader';
import { SessionCallRow, sessionWhen } from './SessionHistory';

const lasted = (seconds) => (seconds === null || seconds === undefined ? '-' : durationLabel(Math.max(1, Math.round(seconds / 60))));

const Tile = ({ label, value }) => (
  <div className="flex flex-col gap-1 rounded-xl border border-line p-4">
    <span className="font-title text-2xl font-semibold leading-8 text-ink">{value}</span>
    <span className="text-sm text-muted">{label}</span>
  </div>
);

// The session's calls, the latest first: null while they are read, false
// when they cannot be.
const useSessionCalls = (id) => {
  const [calls, setCalls] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let current = true;
    setCalls(null);
    getCallSession({ id }).then(
      (session) => {
        if (current) setCalls([...(session?.calls ?? [])].reverse());
      },
      () => {
        if (current) setCalls(false);
      },
    );
    return () => {
      current = false;
    };
  }, [id]);
  return calls;
};

/**
 * What a call session gave: the one just ended (`justEnded`: « Terminer », or
 * its list done) or one of « Mes sessions ». `session` is as the api answers
 * it, { id, startedAt, planned, skipped, stats }, its stats those of
 * sessionStats (calls, reached, rate, rdv, results, seconds). Under its
 * figures, the calls it made, the latest first, all of them. `onBack` goes
 * back to the sessions.
 */
const SessionSummary = ({ session, justEnded = false, onBack }) => {
  const { text } = useText('prospection');
  const stats = session?.stats ?? {};
  const results = stats.results ?? {};
  const calls = useSessionCalls(session?.id);
  return (
    <section className="flex h-full min-w-0 flex-1 flex-col rounded-2xl border border-line bg-white">
      <div className="flex shrink-0 items-start gap-3 p-6 pb-4">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-success-bg text-success-text">
          <CircleCheck size={22} />
        </span>
        <div className="min-w-0">
          <h2 className="font-title text-lg font-semibold leading-6 text-ink">
            {justEnded ? 'Session terminée' : `Session du ${sessionWhen(session)}`}
          </h2>
          <p className="mt-1 text-sm text-muted">
            {stats.calls ?? 0} appel{(stats.calls ?? 0) > 1 ? 's' : ''} sur {session?.planned ?? stats.calls ?? 0} prévus, en{' '}
            {lasted(stats.seconds)}
          </p>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        <div className="grid auto-rows-min grid-cols-2 gap-3">
          <Tile label="Contacts joints" value={stats.reached ?? 0} />
          <Tile label="Taux de joignabilité" value={stats.rate === null || stats.rate === undefined ? '-' : `${Math.round(stats.rate * 100)} %`} />
          <Tile label="Rendez-vous pris" value={stats.rdv ?? 0} />
          <Tile label="Rappels prévus" value={results.callback ?? 0} />
          <Tile label="Sans réponse" value={stats.noAnswer ?? 0} />
          <Tile label="Pas intéressés" value={results.not_interested ?? 0} />
          <Tile label="Laissés de côté" value={session?.skipped ?? 0} />
        </div>
        <section aria-label="Historique des appels" className="mt-4">
          <SectionHeader title="Historique des appels" count={calls ? calls.length : '-'} />
          {calls === null && (
            <div role="status" aria-busy="true" className="flex flex-col divide-y divide-gray-100">
              {[0, 1, 2].map((row) => (
                <div key={row} className="flex items-start gap-3 py-2">
                  <SkeletonLine className="h-3 w-10" />
                  <SkeletonLine className="h-3 w-36" />
                  <SkeletonLine className="h-3 w-24" />
                </div>
              ))}
            </div>
          )}
          {calls === false && <p className="py-3 text-sm text-muted">Historique indisponible.</p>}
          {calls?.length === 0 && <p className="py-3 text-sm text-muted">Aucun appel dans cette session.</p>}
          {calls?.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {calls.map((call) => (
                <SessionCallRow
                  key={call.id}
                  call={{ ...call, outcome: call.result }}
                  text={text}
                  withCompany
                  withDuration={false}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
      <div className="flex shrink-0 border-t border-line p-4">
        <Button variant="secondary" icon={ChevronLeft} onClick={onBack}>
          Mes sessions
        </Button>
      </div>
    </section>
  );
};

export default SessionSummary;
